import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Landmark, History, Coins, PlusCircle, ArrowUpCircle, 
  CheckCircle2, XCircle, Search, Filter, X
} from 'lucide-react'
import useBetStore from '../store/useBetStore'
import Card from '../components/ui/Card'
import Skeleton from '../components/ui/Skeleton'

export default function WalletHistory() {
  const { 
    credits, 
    recharges, 
    withdrawals = [], 
    isLoading, 
    fetchUserRecharges, 
    fetchUserWithdrawals, 
    fetchBalance 
  } = useBetStore()

  const location = useLocation()
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    return tab === 'deposits' || tab === 'withdrawals' ? tab : 'deposits'
  })
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'pendiente', 'aprobado', 'rechazado'
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedImage, setSelectedImage] = useState(null)
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab === 'deposits' || tab === 'withdrawals') {
      setActiveTab(tab)
    }
  }, [location])

  useEffect(() => {
    fetchBalance().catch(() => {})
    fetchUserRecharges().catch(() => {})
    fetchUserWithdrawals().catch(() => {})
  }, [])

  // Filtrado de depósitos
  const filteredDeposits = recharges.filter(req => {
    const matchesStatus = statusFilter === 'all' || req.estado === statusFilter
    const matchesSearch = !searchTerm.trim() || 
      (req.referencia && req.referencia.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesStatus && matchesSearch
  })

  // Filtrado de retiros
  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesStatus = statusFilter === 'all' || w.estado === statusFilter
    const matchesSearch = !searchTerm.trim() || 
      (w.referencia_pago && w.referencia_pago.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesStatus && matchesSearch
  })

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fade-in font-body">
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-2">
            <Landmark className="w-8 h-8 text-cyan animate-float" /> Historial de Depósitos y Retiros
          </h1>
          <p className="text-text-secondary mt-1">
            Consulta el estado de tus solicitudes de recarga de saldo y cobro de créditos.
          </p>
        </div>

        {/* Saldo rápido */}
        <div className="glass px-6 py-3 rounded-2xl border border-border-subtle flex items-center gap-4 self-start md:self-auto">
          <div>
            <p className="text-text-muted text-xs font-medium uppercase tracking-wider">Saldo Disponible</p>
            <p className="text-xl font-display font-bold text-cyan">{credits.toFixed(2)} Bs</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-cyan/15 flex items-center justify-center text-cyan">
            <Coins className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Tabs Selector de Sección */}
      <div className="flex bg-surface-card/25 border border-border-subtle rounded-2xl p-1.5 max-w-md">
        <button
          onClick={() => {
            setActiveTab('deposits')
            setStatusFilter('all')
            setSearchTerm('')
          }}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'deposits'
              ? 'bg-cyan text-navy-dark shadow-lg font-bold'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <PlusCircle className="w-4 h-4" />
          Depósitos / Recargas
        </button>
        <button
          onClick={() => {
            setActiveTab('withdrawals')
            setStatusFilter('all')
            setSearchTerm('')
          }}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'withdrawals'
              ? 'bg-cyan text-navy-dark shadow-lg font-bold'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          <ArrowUpCircle className="w-4 h-4" />
          Retiros
        </button>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="grid sm:grid-cols-3 gap-4 bg-surface-card/40 border border-border-subtle p-4 rounded-2xl glass">
        {/* Búsqueda */}
        <div className="relative col-span-2">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder={
              activeTab === 'deposits'
                ? "Buscar depósitos por referencia..."
                : "Buscar retiros por comprobante..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-navy-dark/45 border border-border-subtle rounded-xl pl-10 pr-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan text-sm transition-colors"
          />
        </div>

        {/* Selector de Estado */}
        <div className="relative">
          <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full bg-navy-dark/45 border border-border-subtle rounded-xl pl-10 pr-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan text-sm transition-colors cursor-pointer appearance-none text-text-secondary"
          >
            <option value="all">Todos los estados</option>
            <option value="pendiente">Pendientes ⏳</option>
            <option value="aprobado">Aprobados ✅</option>
            <option value="rechazado">Rechazados ❌</option>
          </select>
        </div>
      </div>

      {/* Listado de Solicitudes */}
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      ) : activeTab === 'deposits' ? (
        // LISTADO DE DEPÓSITOS
        filteredDeposits.length === 0 ? (
          <Card className="p-12 text-center text-text-muted border border-border-subtle bg-surface-card/30">
            <History className="w-12 h-12 mx-auto mb-3 opacity-40 text-cyan" />
            <h3 className="font-display font-semibold text-lg text-text-primary mb-1">Sin solicitudes</h3>
            <p className="text-sm">
              No tienes solicitudes de recarga que coincidan con los filtros.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredDeposits.map((req, index) => (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.4) }}
                className="bg-surface-card/40 hover:bg-surface-card/65 border border-border-subtle rounded-2xl p-4 flex flex-col gap-2 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                {/* Fila Principal */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border bg-success/10 border-success/20 text-success">
                      <PlusCircle className="w-5 h-5" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold border bg-success/15 border-success/25 text-success">
                          Depósito
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                          req.estado === 'pendiente' 
                            ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' 
                            : req.estado === 'aprobado'
                            ? 'bg-green-500/10 text-green-400 border-green-500/25'
                            : 'bg-red-500/10 text-red-400 border-red-500/25'
                        }`}>
                          {req.estado === 'pendiente' ? 'Pendiente' : (req.estado === 'aprobado' ? 'Aprobado' : 'Rechazado')}
                        </span>
                        <span className="text-[10px] text-text-muted font-mono">
                          ID #{req.id}
                        </span>
                        <span className="text-[10px] text-text-muted">
                          • {new Date(req.creado_en).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <p className="text-sm font-medium text-text-primary truncate font-body">
                          Ref: {req.referencia || 'Sin referencia'}
                        </p>
                        {req.comprobante_ruta && (
                          <button
                            onClick={() => setSelectedImage(`${baseImgUrl}${req.comprobante_ruta}`)}
                            className="text-cyan hover:underline text-xs font-semibold self-start text-left cursor-pointer"
                          >
                            Ver Voucher adjunto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-display font-bold text-base md:text-lg text-success">
                      +{Number(req.monto).toFixed(2)} Bs
                    </span>
                  </div>
                </div>
                {/* Detalle de Rechazo */}
                {req.estado === 'rechazado' && req.motivo_rechazo && (
                  <div className="text-xs text-error font-medium pl-[3.5rem] pt-1.5 border-t border-border-subtle/30 flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>Motivo rechazo: <span className="text-text-secondary font-normal">{req.motivo_rechazo}</span></span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )
      ) : (
        // LISTADO DE RETIROS
        filteredWithdrawals.length === 0 ? (
          <Card className="p-12 text-center text-text-muted border border-border-subtle bg-surface-card/30">
            <History className="w-12 h-12 mx-auto mb-3 opacity-40 text-cyan" />
            <h3 className="font-display font-semibold text-lg text-text-primary mb-1">Sin solicitudes</h3>
            <p className="text-sm">
              No tienes solicitudes de retiro que coincidan con los filtros.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredWithdrawals.map((w, index) => (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.4) }}
                className="bg-surface-card/40 hover:bg-surface-card/65 border border-border-subtle rounded-2xl p-4 flex flex-col gap-2 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                {/* Fila Principal */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border bg-error/10 border-error/20 text-error">
                      <ArrowUpCircle className="w-5 h-5" />
                    </div>
                    <div className="space-y-1 min-w-0 font-body">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold border bg-error/15 border-error/25 text-error">
                          Retiro
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                          w.estado === 'pendiente' 
                            ? 'bg-orange-500/10 text-orange-400 border-orange-500/25' 
                            : w.estado === 'aprobado'
                            ? 'bg-green-500/10 text-green-400 border-green-500/25'
                            : 'bg-red-500/10 text-red-400 border-red-500/25'
                        }`}>
                          {w.estado === 'pendiente' ? 'Pendiente' : (w.estado === 'aprobado' ? 'Aprobado' : 'Rechazado')}
                        </span>
                        <span className="text-[10px] text-text-muted font-mono">
                          ID #{w.id}
                        </span>
                        <span className="text-[10px] text-text-muted">
                          • {new Date(w.creado_en).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => setSelectedImage(`${baseImgUrl}${w.qr_ruta}`)}
                          className="text-cyan hover:underline text-xs font-semibold self-start text-left cursor-pointer"
                        >
                          Ver QR de cuenta bancaria
                        </button>
                        {w.estado === 'aprobado' && w.referencia_pago && (
                          <p className="text-xs text-text-muted font-body">
                            Comprobante: <strong className="text-text-secondary font-mono">{w.referencia_pago}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-display font-bold text-base md:text-lg text-error">
                      -{Number(w.monto).toFixed(2)} Bs
                    </span>
                  </div>
                </div>
                {/* Detalle de Rechazo */}
                {w.estado === 'rechazado' && w.motivo_rechazo && (
                  <div className="text-xs text-error font-medium pl-[3.5rem] pt-1.5 border-t border-border-subtle/30 flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>Motivo rechazo: <span className="text-text-secondary font-normal">{w.motivo_rechazo}</span></span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )
      )}
      {/* Modal de Previsualización de Imagen */}
      <AnimatePresence>
        {selectedImage && (
          <div 
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-lg w-full bg-[#0E1626] border border-border-subtle rounded-2xl p-5 shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Botón cerrar */}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 p-1.5 rounded-lg bg-surface-card/85 text-text-muted hover:text-text-primary border border-border-subtle hover:bg-surface-card transition-all z-10 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>

              <h3 className="text-sm font-display font-semibold text-text-primary mb-3">
                Previsualización de Documento
              </h3>

              <div className="w-full flex items-center justify-center bg-black/40 rounded-xl p-3 border border-border-subtle/50">
                <img
                  src={selectedImage}
                  alt="Comprobante o Código QR"
                  className="max-h-[60vh] max-w-full object-contain rounded-lg"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
